"use client";

import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { usePageIdle } from "@/hooks/use-page-idle";
import { getProductForQuickViewBySlug, type QuickViewPayload } from "@/lib/widgets/actions";
import type { HexMap, ProductData } from "./types";

// Fetched when the page goes idle (see the effect below) so the first open is
// instant, but kept off the first paint.
const loadDialog = () => import("./QuickViewDialog");
const QuickViewDialog = dynamic(() => loadDialog().then(m => m.QuickViewDialog), {
  ssr: false
});

/**
 * Global, URL-driven quick view.
 *
 * The open product lives in the `?quickview=<slug>` query param so the modal
 * has its own shareable/copyable URL — distinct from the PDP route
 * (`/products/<slug>`). One dialog is rendered for the whole storefront:
 *  - Cards call openQuickView(slug, { product, hexMap }) — instant, preloaded.
 *  - A shared/refreshed `?quickview=<slug>` URL is resolved by slug on mount.
 *  - Back button / close strips the param and dismisses the modal.
 */

const PARAM = "quickview";
type Preload = {
  product: ProductData;
  hexMap: HexMap;
};
type QuickViewContextValue = {
  openQuickView: (key: string, preload?: Preload) => void;
  /**
   * False when no provider is mounted (the admin live preview). Callers that
   * render a quick-view trigger use this to fall back to a real link rather
   * than a control that silently does nothing.
   */
  enabled: boolean;
};

// Default to a no-op opener so cards rendered OUTSIDE a provider (e.g. the
// admin page-builder live preview) don't crash — they simply can't open a
// quick view, which is fine in those contexts.
const QuickViewContext = createContext<QuickViewContextValue>({
  openQuickView: () => {},
  enabled: false
});
export function useQuickView() {
  return useContext(QuickViewContext);
}
function readKey(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(PARAM);
}
export function QuickViewProvider(t0) {
  const $ = _c(19);
  const {
    children
  } = t0;
  const [activeKey, setActiveKey] = useState(null);
  const [payload, setPayload] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cacheRef = useRef(t1);
  const pageIdle = usePageIdle();
  let t2;
  let t3;
  if ($[1] !== pageIdle) {
    t2 = () => {
      if (pageIdle) {
        loadDialog();
      }
    };
    t3 = [pageIdle];
    $[1] = pageIdle;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const sync = () => setActiveKey(readKey());
      sync();
      window.addEventListener("popstate", sync);
      return () => window.removeEventListener("popstate", sync);
    };
    t5 = [];
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== activeKey) {
    t6 = () => {
      if (!activeKey) {
        setPayload(null);
        return;
      }
      const cached = cacheRef.current.get(activeKey);
      if (cached) {
        setPayload(cached);
        return;
      }
      let cancelled = false;
      setPayload(null);
      getProductForQuickViewBySlug(activeKey).then(p => {
        if (cancelled) {
          return;
        }
        if (p) {
          cacheRef.current.set(activeKey, p);
          setPayload(p);
        } else {
          const url = new URL(window.location.href);
          url.searchParams.delete(PARAM);
          window.history.replaceState(null, "", url.toString());
          setActiveKey(null);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t7 = [activeKey];
    $[6] = activeKey;
    $[7] = t6;
    $[8] = t7;
  } else {
    t6 = $[7];
    t7 = $[8];
  }
  useEffect(t6, t7);
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (key, preload) => {
      if (preload) {
        cacheRef.current.set(key, {
          product: preload.product,
          hexMap: preload.hexMap
        });
      }
      if (readKey() === key) {
        setActiveKey(key);
        return;
      }
      const url_0 = new URL(window.location.href);
      url_0.searchParams.set(PARAM, key);
      window.history.pushState({
        quickView: key
      }, "", url_0.toString());
      setActiveKey(key);
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const openQuickView = t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      if (window.history.state?.quickView) {
        window.history.back();
      } else {
        const url_1 = new URL(window.location.href);
        url_1.searchParams.delete(PARAM);
        window.history.replaceState(null, "", url_1.toString());
        setActiveKey(null);
      }
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const closeQuickView = t9;
  let t10;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      openQuickView,
      enabled: true
    };
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== activeKey || $[13] !== payload?.hexMap || $[14] !== payload?.product) {
    t11 = activeKey && <QuickViewDialog key={activeKey} open={true} productId={payload?.product.id} preloadedProduct={payload?.product} preloadedHexMap={payload?.hexMap} onClose={closeQuickView} />;
    $[12] = activeKey;
    $[13] = payload?.hexMap;
    $[14] = payload?.product;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== children || $[17] !== t11) {
    t12 = <QuickViewContext.Provider value={t10}>{children}{t11}</QuickViewContext.Provider>;
    $[16] = children;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}